import { Loading } from 'components/loading/Loading';
import React, { useEffect, useState } from 'react';
import { fetchRouteDetail, markRouteAsDelivered , markRouteAsPacked , removeRouteFromPickPackList } from 'services';
import { DATE_FORMATS, ROUTE_STATUS, baseURL, formatDate, getDistanceInMiles, getSecondsDurationInHoursMins, showError, showSuccess } from 'utils';
import styles from './index.module.scss';
import BackButton from 'components/BackButton';
import { Orders } from 'pages/orders';
import AddOrderToRouteModal from 'components/AddOrderToRouteModal';
import ActionButton from 'components/ActionButton';
import AddRouteToPickPackListModal from 'components/AddRouteToPickPackListModal';
import QRCodeGenerator from 'components/QRCodeGenerator';
import Currency from 'components/Currency/Currency';
import _ from 'lodash';

const RouteDetails = props => {
  const { routeId } = props.match.params;
  const [loading, setLoading] = useState<boolean>(true);
  const [routeLoading,setRouteLoading] = useState<boolean>(false);
  const [routeInfo, setRouteInfo] = useState<Record<string, any>>({});
  const [routeOrders, setRouteOrders] = useState<Record<string, any>[]>([]);
  const [addToRouteModalOpen, setAddToRouteModalOpen] = useState<boolean>(false);
  const [orderIdForModal, setOrderIdForModal] = useState<string>('');
  const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([]);
  const [multipleOrders, setMultipleOrders] = useState<boolean>(false);
  const [addToPickPackListModalOpen, setAddtoPickPackListModalOpen] = useState<boolean>(false);
  const [selectedRouteIds, setSelectedRouteIds] = useState<string[]>([]);
  const [pickPackActionLoading, setPickPackActionLoading] = useState<boolean>(false);

  const fetchRoutesDetailsFn = async () => {
    try {
      const response = await fetchRouteDetail(routeId);
      setRouteInfo(response.route);
      setRouteOrders(response.orders);
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchRoutesDetailsFn();
  }, []);

  const setSelectedOrders = (data: Record<string, any>) => {
    const { selectedRows } = data;
    setSelectedOrderIds(selectedRows.map(row => row.objectId));
  };

  const removeRouteFromPickPackListFn = async () => {
    setPickPackActionLoading(true);
    try {
      await removeRouteFromPickPackList(routeId);
      showSuccess('Route removed from Pick Pack List successfully');
      fetchRoutesDetailsFn();
    } catch (e) {
      showError(e);
    } finally {
      setPickPackActionLoading(false);
    }
  };

  const getQRCodeURLs = () =>
    routeOrders.map(order => `${baseURL}/orders/${order.objectId}`);
  
  const getPickPackAction = () => {
    const handleAddRoute = () => {
      setSelectedRouteIds([routeId]);
      setAddtoPickPackListModalOpen(true);
    };

    return (
      <ActionButton
        text={routeInfo.pickPackList ? 'Remove' : 'Add'}
        active={!pickPackActionLoading}
        isLoading={pickPackActionLoading}
        onClick={routeInfo.pickPackList ? removeRouteFromPickPackListFn : handleAddRoute}
        customClass={routeInfo.pickPackList ? styles['delete'] : ''}
      />
    );
  };

  const markRouteAsDeliveredFn = async () => {
    setRouteLoading(true);
    await markRouteAsDelivered(routeId);
    setRouteLoading(false);
    fetchRoutesDetailsFn(); 
  };

  const markRouteAsPackedFn = async () => {
    setRouteLoading(true);
    await markRouteAsPacked(routeId);
    setRouteLoading(false);
    fetchRoutesDetailsFn(); 
  };

  const markRoutesButtons = () => {
    if(routeInfo.status === ROUTE_STATUS.PACKED){
      return (
        <ActionButton
          text={'Mark As Delivered'}
          active={!routeLoading}
          isLoading={routeLoading}
          onClick={markRouteAsDeliveredFn}
        />
      );
    }else if(routeInfo.status !== ROUTE_STATUS.DELIVERED){
      return (
        <ActionButton
          text={'Mark As Packed'}
          active={!routeLoading}
          isLoading={routeLoading}
          onClick={markRouteAsPackedFn}
        />
      );
    }
    return null;
  };

  const getRouteOrders = () => (
    <div className={styles['route-orders']}>
      <div className={styles['orders-header']}>
        <div className={styles['subheading']}>Orders</div>
        <div className='d-flex mr-4'>
          <ActionButton
            text={'View as driver'}
            active={true}
            isLoading={false}
            onClick={() => {
              props.history.push(`/routes/${routeId}/orders`);
            }}
          />
          {selectedOrderIds.length > 0 && (
            <ActionButton
              text={'Add Orders To Route'}
              active={true}
              isLoading={false}
              onClick={() => {
                setMultipleOrders(true);
                setAddToRouteModalOpen(true);
              }}
              customClass='ml-3'
            />
          )}
        </div>
      </div>
      <Orders
        {...props}
        data={routeOrders}
        total={routeOrders.length}
        dataUpdated={loading}
        refreshData={fetchRoutesDetailsFn}
        setSelectedOrders={setSelectedOrders}
        selectableRows={true}
      />
    </div>
  );
  
  const getPickPackListInfo = () => (
    <div className={styles['pickpacklist-details-section']}>
      <div className={styles['pickpack-details-header']}>
        <div className={styles['subheading']}>Pick Pack List Details</div>
        {getPickPackAction()}
      </div>
      {routeInfo.pickPackList ? (
        <div className={styles['pickpacklist-info']}>
          <div className={styles['info']}>
            <div className={styles['info-group']}>
              <div className={styles['label']}>Pick Pack List ID</div>
              <a
                href={`/pick-packs/${routeInfo.pickPackList.objectId}`}
                className={styles['detail']}
              >
                {routeInfo.pickPackList.objectId}
              </a>
            </div>
            <div className={styles['info-group']}>
              <div className={styles['label']}>Name</div>
              <div className={styles['detail']}>{routeInfo.pickPackList.name || '-'}</div>
            </div>
            <div className={styles['info-group']}>
              <div className={styles['label']}>Owner</div>
              <div className={styles['detail']}>
                {routeInfo.pickPackList.owner?.full_name || routeInfo.pickPackList.owner?.username || '-'}
              </div>
            </div>
          </div>
          <div className={styles['info']}>
            <div className={styles['info-group']}>
              <div className={styles['label']}>Pick Pack List No.</div>
              <div className={styles['details']}>{routeInfo.pickPackList.pickPackListNo}</div>
              {/* <div className={styles['detail']}>{formatDate(routeInfo.pickPackList.deliveryDate, DATE_FORMATS.MM_DD_YYYY)}</div> */}
            </div>
            <div className={styles['info-group']}>
              <div className={styles['label']}>Created On</div>
              <div className={styles['detail']}>{formatDate(routeInfo.pickPackList.createdAt, DATE_FORMATS.MM_DD_YYYY_hh_mm_A)}</div>
            </div>
            <div className={styles['info-group']}>
              <div className={styles['label']}>Locked</div>
              <div className={styles['detail']}>{routeInfo.pickPackList.locked?.toString() || '-'}</div>
            </div>
          </div>
        </div>
      ) : (
        <div className={styles['empty-list-message']}>No Pick Pack List found</div>
      )}
    </div>
  );
  
  const getRouteBasicInfo = () => {
    const distanceInMiles = getDistanceInMiles(routeInfo.estimatedRoutingDistance);
    const durationInHoursMinutes = getSecondsDurationInHoursMins(routeInfo.estimatedRoutingDuration);
    return (
      <div className={styles['basic-details']}>
        <div className={'d-flex w-100 justify-content-between'}>
          <div className={styles['details-column']}>
            <div className={styles['info-group']}>
              <div className={styles['label']}>Route ID</div>
              <div className={`${styles['detail']} ${styles['id']}`}>{routeInfo.objectId}</div>
            </div>
            <div className={styles['info-group']}>
              <div className={styles['label']}>Name</div>
              <div className={styles['detail']}>{routeInfo.name}</div>
            </div>
            <div className={styles['info-group']}>
              <div className={styles['label']}>Created At</div>
              <div className={styles['detail']}>{formatDate(routeInfo.createdAt, DATE_FORMATS.MM_DD_YYYY_hh_mm_A)}</div>
            </div>
            <div className={styles['info-group']}>
              <div className={styles['label']}>Earning</div>
              <div className={styles['detail']}>
                {routeInfo.routeEarning ? (
                  <Currency amount={parseFloat(routeInfo.routeEarning.toFixed(2))} />
                ) : '-'}
              </div>
            </div>
            <div className={styles['info-group']}>
              <div className={styles['label']}>Distance</div>
              <div className={styles['detail']}>{distanceInMiles}</div>
            </div>
            <div className={styles['info-group']}>
              <div className={styles['label']}>Duration</div>
              <div className={styles['detail']}>{durationInHoursMinutes}</div>
            </div>
            <div className={styles['info-group']}>
              <div className={styles['label']}>Comment</div>
              <div className={styles['detail']}>{routeInfo.comment || '-'}</div>
            </div>
            <div className={styles['info-group']}>
              <div className={styles['label']}>Comment</div>
              <div className={styles['detail']}>{routeInfo.comment || '-'}</div>
            </div>
            <div className={styles['info-group']}>
              <div className={styles['label']}>Route URL</div>
              <div className={styles['detail']}>{routeInfo.routeURL || '-'}</div>
            </div>
          </div>
          <div className={styles['details-column']}>
            <div className={styles['info-group']}>
              <div className={styles['label']}>Status</div>
              <div className={`${styles['status']} ${styles[`${routeInfo.status}`]}`}>{routeInfo.status}</div>
            </div>
            {routeInfo.locked ? (
              <div className={styles['info-group']}>
                <div className={styles['label']}>Locked</div>
                <div className={styles['detail']}>{routeInfo.locked.toString()}</div>
              </div>
            ) : null}
            <div className={styles['info-group']}>
              <div className={styles['label']}>Delivery Date</div>
              <div className={styles['detail']}>{formatDate(routeInfo.deliveryDateTime, DATE_FORMATS.MM_DD_YYYY)}</div>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  const getHeader = () => (
    <div className={styles['header']}>
      <div className={styles['heading']}>Route Details</div>
      <div className={styles['header-buttons']}>
        <BackButton />
        {markRoutesButtons()}
        {!!routeOrders.length ? (
          <ActionButton
            text={'Print Orders QR Codes'}
            active={true}
            isLoading={false}
            onClick={() => window.print()}
          />
        ) : null}
      </div>
    </div>
  );
  
  if (loading) return <Loading />;
  
  return (
    <div
      className={`${'main-container'}`}
    >
      {addToRouteModalOpen && (
        <AddOrderToRouteModal
          orderIds={multipleOrders ? selectedOrderIds : orderIdForModal}
          onSuccess={() => {
            setSelectedOrderIds([]);
            fetchRoutesDetailsFn();
          }}
          toggleModal={() => setAddToRouteModalOpen(!addToRouteModalOpen)}
        />
      )}
      {addToPickPackListModalOpen && (
        <AddRouteToPickPackListModal
          routeIds={selectedRouteIds}
          onSuccess={() => {
            setSelectedRouteIds([]);
            fetchRoutesDetailsFn();
          }}
          toggleModal={() => {
            setSelectedRouteIds([]);
            setAddtoPickPackListModalOpen(!addToPickPackListModalOpen);
          }}
        />
      )}
      <div id={'main-container'} className={`${styles['main-container']}`}>
        {getHeader()}
        {getRouteBasicInfo()}
        {getPickPackListInfo()}
        {getRouteOrders()}
      </div>
      <div id={'qr-code-container'} className={styles['qr-code-container']}>
        <QRCodeGenerator
          qrCodeURLs={getQRCodeURLs()}
        />
      </div>
    </div>
  );
};

export { RouteDetails };